import React from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Settings, Shield, Database, Clock, Trash2, ExternalLink, Mail } from 'lucide-react';
import SEO from '../components/SEO';
import './Cookies.css';

const Cookies = () => {
  const currentDate = new Date().toLocaleDateString('es-ES', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Política de Cookies - Artelaser3D",
    "description": "Política de cookies de Artelaser3D. Información sobre el uso de cookies en nuestro sitio web de grabado láser e impresión 3D en Asturias.",
    "url": "https://artelaser3d.es/cookies",
    "lastReviewed": new Date().toISOString().split('T')[0],
    "mainEntity": {
      "@type": "Organization",
      "name": "Artelaser3D",
      "url": "https://artelaser3d.es"
    }
  };

  const cookieTypes = [
    {
      id: 'necesarias',
      icon: <Shield size={24} />,
      title: 'Cookies Técnicas y Necesarias',
      description: 'Son esenciales para el funcionamiento del sitio web. Sin estas cookies, el sitio no puede funcionar correctamente.',
      examples: [
        'Cookies de sesión: Mantienen la sesión del usuario activa',
        'Cookies de seguridad: Protegen contra fraudes y ataques',
        'Cookies de preferencias: Recuerdan configuraciones básicas del usuario'
      ],
      duration: 'Sesión / Persistentes',
      mandatory: true
    },
    {
      id: 'analiticas',
      icon: <Database size={24} />,
      title: 'Cookies Analíticas',
      description: 'Nos permiten analizar el comportamiento de los usuarios para mejorar nuestros servicios.',
      examples: [
        'Google Analytics: Estadísticas de uso del sitio web',
        'Cookies de rendimiento: Medición de tiempos de carga',
        'Cookies de navegación: Análisis de rutas de usuario'
      ],
      duration: '2 años máximo',
      mandatory: false
    },
    {
      id: 'funcionales',
      icon: <Settings size={24} />,
      title: 'Cookies Funcionales',
      description: 'Permiten recordar preferencias para ofrecer una experiencia personalizada.',
      examples: [
        'Idioma preferido',
        'Configuración de visualización',
        'Recordar datos de formularios'
      ],
      duration: '1 año',
      mandatory: false
    }
  ];

  const thirdPartyCookies = [
    {
      provider: 'Google Analytics',
      purpose: 'Análisis y estadísticas web',
      cookies: ['_ga', '_gid', '_gat'],
      moreInfo: 'https://policies.google.com/privacy'
    },
    {
      provider: 'React / React Router',
      purpose: 'Funcionalidad de la aplicación',
      cookies: ['session_storage', 'local_storage'],
      moreInfo: null
    }
  ];

  return (
    <>
      <SEO 
        title="Política de Cookies"
        description="Política de cookies de Artelaser3D. Información detallada sobre el uso de cookies en nuestro sitio web de grabado láser e impresión 3D en Asturias."
        keywords="política de cookies Artelaser3D, cookies grabado láser Asturias, privacidad web impresión 3D, gestionar cookies Asturias"
        canonicalUrl="https://artelaser3d.es/cookies"
        schema={schema}
      />

      {/* Hero Section */}
      <section className="page-hero page-hero--cookies">
        <div className="page-hero__container">
          <span className="page-hero__badge">Privacidad</span>
          <h1 className="page-hero__title">
            Política de
            <span className="text-primary"> Cookies</span>
          </h1>
          <p className="page-hero__description">
            Información detallada sobre el uso de cookies en nuestro sitio web. 
            Tu privacidad es importante para nosotros.
          </p>
          <p className="page-hero__updated">
            Última actualización: {currentDate}
          </p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="section section--cookies-intro section--visible">
        <div className="section__container">
          <div className="cookies-intro-card">
            <div className="cookies-intro-icon">
              <Cookie size={40} />
            </div>
            <div className="cookies-intro-content">
              <h2>¿Qué son las cookies?</h2>
              <p>
                Las cookies son pequeños archivos de texto que se almacenan en tu dispositivo 
                (ordenador, tablet o móvil) cuando visitas un sitio web. Estas cookies nos permiten 
                recordar tus preferencias, analizar el uso del sitio y mejorar tu experiencia de navegación.
              </p>
              <p>
                En <strong>Artelaser3D</strong> utilizamos cookies de manera responsable para 
                ofrecerte el mejor servicio posible en nuestra web de grabado láser e impresión 3D. 
                Puedes gestionar tus preferencias de cookies en cualquier momento.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cookie Types Section */}
      <section className="section section--cookie-types section--visible">
        <div className="section__container">
          <header className="section__header">
            <span className="section__badge">Tipos de Cookies</span>
            <h2>Cookies que utilizamos</h2>
            <p>A continuación te detallamos las cookies que utilizamos y su finalidad:</p>
          </header>

          <div className="cookies-types-grid">
            {cookieTypes.map((type) => (
              <article key={type.id} id={type.id} className="cookie-type-card">
                <div className="cookie-type-header">
                  <div className="cookie-type-icon">{type.icon}</div>
                  <div className="cookie-type-title-wrapper">
                    <h3>{type.title}</h3>
                    {type.mandatory ? (
                      <span className="cookie-type-badge cookie-type-badge--required">Obligatoria</span>
                    ) : (
                      <span className="cookie-type-badge cookie-type-badge--optional">Opcional</span>
                    )}
                  </div>
                </div>
                
                <div className="cookie-type-content">
                  <p className="cookie-type-description">{type.description}</p>
                  
                  <div className="cookie-examples">
                    <h4>Ejemplos:</h4>
                    <ul>
                      {type.examples.map((example, idx) => (
                        <li key={idx}>{example}</li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="cookie-meta">
                    <span className="cookie-duration">
                      <Clock size={16} />
                      Duración: {type.duration}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Third Party Cookies Section */}
      <section className="section section--third-party section--visible">
        <div className="section__container">
          <header className="section__header">
            <span className="section__badge">Cookies de Terceros</span>
            <h2>Servicios externos</h2>
            <p>Algunas cookies son establecidas por servicios de terceros que utilizamos:</p>
          </header>

          <div className="third-party-grid">
            {thirdPartyCookies.map((service, index) => (
              <article key={index} className="third-party-card">
                <div className="third-party-header">
                  <h3>{service.provider}</h3>
                  {service.moreInfo && (
                    <a 
                      href={service.moreInfo}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="third-party-link"
                    >
                      Más información
                      <ExternalLink size={16} />
                    </a>
                  )}
                </div>
                
                <p className="third-party-purpose">
                  <strong>Finalidad:</strong> {service.purpose}
                </p>
                
                <div className="third-party-cookies">
                  <strong>Cookies utilizadas:</strong>
                  <div className="cookie-tags">
                    {service.cookies.map((cookie, idx) => (
                      <span key={idx} className="cookie-tag">{cookie}</span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Management Section */}
      <section className="section section--cookies-management section--visible">
        <div className="section__container">
          <div className="management-grid">
            <article className="management-card management-card--browser">
              <div className="management-icon">
                <Settings size={32} />
              </div>
              <h3>Gestión desde tu navegador</h3>
              <p>
                Puedes configurar tu navegador para aceptar o rechazar cookies. Consulta 
                las instrucciones específicas para tu navegador:
              </p>
              <ul className="browser-list">
                <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
                <li><a href="https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
                <li><a href="https://support.apple.com/es-es/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a></li>
                <li><a href="https://support.microsoft.com/es-es/help/4027947/microsoft-edge-delete-cookies" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
              </ul>
            </article>

            <article className="management-card management-card--delete">
              <div className="management-icon">
                <Trash2 size={32} />
              </div>
              <h3>Eliminar cookies existentes</h3>
              <p>
                Si deseas eliminar las cookies que ya están almacenadas en tu dispositivo, 
                puedes hacerlo desde la configuración de tu navegador. Ten en cuenta que 
                esto puede afectar el funcionamiento del sitio.
              </p>
              <p className="management-note">
                <strong>Nota:</strong> Al eliminar cookies técnicas, es posible que 
                necesites iniciar sesión nuevamente o reconfigurar algunas preferencias.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* Legal Info Section */}
      <section className="section section--cookies-legal section--visible">
        <div className="section__container">
          <div className="cookies-legal-grid">
            <article className="cookies-legal-card">
              <h3>Base Legal</h3>
              <p>
                El uso de cookies se realiza conforme al Reglamento (UE) 2016/679 (GDPR), 
                la Ley 34/2002 de Servicios de la Sociedad de la Información (LSSI) y la 
                normativa vigente en materia de protección de datos.
              </p>
            </article>

            <article className="cookies-legal-card">
              <h3>Actualizaciones</h3>
              <p>
                Nos reservamos el derecho de modificar esta política de cookies en cualquier 
                momento. Te recomendamos revisarla periódicamente para estar informado de 
                posibles cambios.
              </p>
            </article>

            <article className="cookies-legal-card">
              <h3>Contacto</h3>
              <p>
                Para cualquier consulta sobre nuestra política de cookies:
              </p>
              <ul className="legal-contact-list">
                <li>
                  <Mail size={18} />
                  <span>Email: info@artelaser3d.es</span>
                </li>
              </ul>
            </article>

            <article className="cookies-legal-card cookies-legal-card--highlight">
              <h3>Tu consentimiento</h3>
              <p>
                Al continuar navegando por nuestro sitio web, aceptas el uso de cookies 
                conforme a esta política. Puedes modificar tus preferencias en cualquier momento.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section section--cta section--cta--cookies section--visible">
        <div className="cta__content">
          <h2 className="cta__title--cookies">¿Tienes dudas sobre cookies?</h2>
          <p className="cta__description--cookies">
            Si necesitas más información sobre nuestra política de cookies o cómo gestionarlas, 
            no dudes en contactarnos. Estamos aquí para ayudarte.
          </p>
          <div className="cta__buttons">
            <Link to="/contacto" className="btn btn--primary btn--lg">
              Contactar
            </Link>
            <Link to="/terminos" className="btn btn--outline btn--lg">
              Ver Términos y Condiciones
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Cookies;
